import { GameState } from '../types/game';
import { 
  checkInningsComplete, 
  checkLastManStands,
  determineWinner,
  calculateTarget,
  getNextBowlerName,
  getNextBatterName
} from './gameLogic';


/** Dismissal types credited to the bowler. Matched on the value or label, case-insensitively. */
export const BOWLER_CREDITED_DISMISSALS = new Set(['bowled', 'caught', 'caught_behind', 'caught behind', 'lbw', 'stumped', 'hit_wicket', 'hit wicket']);
export function creditsBowler(dismissalType: string): boolean {
  return BOWLER_CREDITED_DISMISSALS.has((dismissalType || '').trim().toLowerCase());
}

interface ScoringResult {
  newState: GameState;
  shouldStartSecondInnings: boolean;
}

export function handleScoringAction(
  gameState: GameState, 
  action: string, 
  runs: number | undefined,
  bowlingTeamPlayers: string[]
): ScoringResult {
  const newState = { ...gameState };
  const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;

  switch (action) {
    case "runs":
      if (runs !== undefined) {
        currentInnings.runs += runs;
        currentInnings.totalBalls += 1;
        currentInnings.ballsThisOver += 1;

        currentInnings.bowler.runsConceded += runs;
        currentInnings.bowler.ballsBowled += 1;

        if (currentInnings.batter1.onStrike && !currentInnings.batter1.isRetired && currentInnings.batter1.name !== "Last Man Stands") {
          currentInnings.batter1.runs += runs;
          currentInnings.batter1.balls += 1;
        } else if (currentInnings.batter2.onStrike && !currentInnings.batter2.isRetired && currentInnings.batter2.name !== "Last Man Stands") {
          currentInnings.batter2.runs += runs;
          currentInnings.batter2.balls += 1;
        }

        if (runs % 2 === 1 && !currentInnings.isLastManStands) {
          currentInnings.batter1.onStrike = !currentInnings.batter1.onStrike;
          currentInnings.batter2.onStrike = !currentInnings.batter2.onStrike;
        }

        currentInnings.currentOverBalls.push(runs);

        // Check if over is complete (standard balls per over)
        if (currentInnings.ballsThisOver === newState.matchSettings.ballsPerOver) {
          endOver(currentInnings, bowlingTeamPlayers);
        }
      }
      break;

    case "no_runs":
      currentInnings.totalBalls += 1;
      currentInnings.ballsThisOver += 1;
      currentInnings.bowler.ballsBowled += 1;

      if (currentInnings.batter1.onStrike && !currentInnings.batter1.isRetired && currentInnings.batter1.name !== "Last Man Stands") {
        currentInnings.batter1.balls += 1;
      } else if (currentInnings.batter2.onStrike && !currentInnings.batter2.isRetired && currentInnings.batter2.name !== "Last Man Stands") {
        currentInnings.batter2.balls += 1;
      }

      currentInnings.currentOverBalls.push(0);

      // Check if over is complete (standard balls per over)
      if (currentInnings.ballsThisOver === newState.matchSettings.ballsPerOver) {
        endOver(currentInnings, bowlingTeamPlayers);
      }
      break;

    case "wide":
    case "no_ball":
    case "bye":
    case "leg_bye":
      currentInnings.runs += 1;
      if (action === "wide" || action === "no_ball") {
        currentInnings.bowler.runsConceded += 1;
        // Extras don't count towards legal deliveries (ballsThisOver), but they are tracked in currentOverBalls
      } else {
        // Byes and leg byes count as legal deliveries
        currentInnings.totalBalls += 1;
        currentInnings.ballsThisOver += 1;
        currentInnings.bowler.ballsBowled += 1;
        
        if (currentInnings.batter1.onStrike && !currentInnings.batter1.isRetired && currentInnings.batter1.name !== "Last Man Stands") {
          currentInnings.batter1.balls += 1;
        } else if (currentInnings.batter2.onStrike && !currentInnings.batter2.isRetired && currentInnings.batter2.name !== "Last Man Stands") {
          currentInnings.batter2.balls += 1;
        }
      }
      
      currentInnings.currentOverBalls.push(action === "wide" ? "WD" : action === "no_ball" ? "NB" : action === "bye" ? "B" : "LB");
      
      // Check if over is complete for byes/leg byes (standard balls per over)
      if ((action === "bye" || action === "leg_bye") && currentInnings.ballsThisOver === newState.matchSettings.ballsPerOver) {
        endOver(currentInnings, bowlingTeamPlayers);
      }
      break;
  }

  // Check if innings is complete
  if (checkInningsComplete(currentInnings, newState)) {
    if (newState.currentInnings === 1) {
      if (currentInnings.currentOverBalls.length > 0) {
        currentInnings.previousOvers.push([...currentInnings.currentOverBalls]);
      }
      return { newState, shouldStartSecondInnings: true };
    } else {
      newState.gameComplete = true;
      newState.winner = determineWinner(newState);
    }
  }

  // Check if target is reached in second innings
  if (newState.currentInnings === 2 && newState.innings2) {
    const target = calculateTarget(newState.innings1);
    if (newState.innings2.runs >= target) {
      newState.gameComplete = true;
      newState.winner = determineWinner(newState);
    }
  }

  return { newState, shouldStartSecondInnings: false };
}

export function handleWicketAction(
  gameState: GameState,
  wicketDetails: {
    dismissalType: string;
    batterOut: string;
    bowler: string;
    fielder?: string;
    runsBeforeWicket?: {
      runs: number;
      type: 'batting' | 'leg_bye' | 'bye' | 'wide' | 'no_ball';
    };
  },
  battingTeamPlayers: string[],
  bowlingTeamPlayers: string[]
): ScoringResult {
  const newState = { ...gameState };
  const currentInnings = gameState.currentInnings === 1 ? newState.innings1 : newState.innings2!;
  const currentBatters: [string, string] = [currentInnings.batter1.name, currentInnings.batter2.name];
  
  // Handle runs before wicket (for run outs)
  if (wicketDetails.runsBeforeWicket && wicketDetails.runsBeforeWicket.runs > 0) {
    const runsBefore = wicketDetails.runsBeforeWicket.runs;
    const runType = wicketDetails.runsBeforeWicket.type;
    
    // Add the runs to team total
    currentInnings.runs += runsBefore;
    
    // Handle different types of runs
    if (runType === 'batting') {
      // Normal batting runs
      if (currentInnings.batter1.onStrike && !currentInnings.batter1.isRetired && currentInnings.batter1.name !== "Last Man Stands") {
        currentInnings.batter1.runs += runsBefore;
      } else if (currentInnings.batter2.onStrike && !currentInnings.batter2.isRetired && currentInnings.batter2.name !== "Last Man Stands") {
        currentInnings.batter2.runs += runsBefore;
      }
      currentInnings.bowler.runsConceded += runsBefore;
    } else if (runType === 'wide' || runType === 'no_ball') {
      // Extras from bowler
      currentInnings.bowler.runsConceded += runsBefore;
    }
    // For byes and leg byes, no additional stats are added to batter or bowler
    
    // Handle strike rotation for odd runs (except for run outs where strike doesn't matter)
    if (runsBefore % 2 === 1 && !currentInnings.isLastManStands) {
      currentInnings.batter1.onStrike = !currentInnings.batter1.onStrike;
      currentInnings.batter2.onStrike = !currentInnings.batter2.onStrike;
    }
    
    // Add to over balls based on run type
    if (runType === 'wide') {
      currentInnings.currentOverBalls.push('WD');
    } else if (runType === 'no_ball') {
      currentInnings.currentOverBalls.push('NB');
    } else if (runType === 'bye') {
      currentInnings.currentOverBalls.push('B');
    } else if (runType === 'leg_bye') {
      currentInnings.currentOverBalls.push('LB');
    } else {
      // Normal batting runs
      currentInnings.currentOverBalls.push(runsBefore);
    }
  }
  
  // Record the wicket
  const batterStats = wicketDetails.batterOut === currentInnings.batter1.name ? currentInnings.batter1 : currentInnings.batter2;
  currentInnings.wicketRecords.push({
    batterOut: wicketDetails.batterOut,
    dismissalType: wicketDetails.dismissalType,
    bowler: wicketDetails.bowler,
    fielder: wicketDetails.fielder,
    runs: batterStats.runs,
    balls: batterStats.balls,
    overNumber: currentInnings.currentOver,
    runsBeforeWicket: wicketDetails.runsBeforeWicket,
  });

  // Update game state for the wicket
  currentInnings.wicketsDown += 1;
  currentInnings.totalBalls += 1;
  currentInnings.ballsThisOver += 1;
  currentInnings.bowler.ballsBowled += 1;
  // Only the bowler's dismissals credit the bowler (Laws / PlayHQ; mirrors
  // RamperCricket's WicketHelpers.creditsBowler): a run out, obstructing the
  // field, retirement etc. is a wicket for the side, not for the bowler.
  if (creditsBowler(wicketDetails.dismissalType)) {
    currentInnings.bowler.wicketsTaken += 1;
  }
  currentInnings.currentOverBalls.push('W');

  // Update balls faced for the dismissed batter
  if (wicketDetails.batterOut === currentInnings.batter1.name && !currentInnings.batter1.isRetired && currentInnings.batter1.name !== "Last Man Stands") {
    currentInnings.batter1.balls += 1;
  } else if (wicketDetails.batterOut === currentInnings.batter2.name && !currentInnings.batter2.isRetired && currentInnings.batter2.name !== "Last Man Stands") {
    currentInnings.batter2.balls += 1;
  }

  if (wicketDetails.bowler !== currentInnings.bowler.name) {
    currentInnings.bowler.name = wicketDetails.bowler;
  }

  // Replace batter with next batter or handle Last Man Stands
  if (wicketDetails.batterOut === currentInnings.batter1.name) {
    if (checkLastManStands(currentInnings, gameState)) {
      currentInnings.isLastManStands = true;
      currentInnings.batter1 = {
        name: "Last Man Stands",
        runs: 0,
        balls: 0,
        onStrike: false,
        isRetired: false,
      };
      currentInnings.batter2.onStrike = true;
    } else {
      currentInnings.batter1 = {
        name: getNextBatterName(currentBatters, currentInnings, battingTeamPlayers),
        runs: 0,
        balls: 0,
        onStrike: false,
        isRetired: false,
      };
      currentInnings.batter2.onStrike = true;
    }
  } else {
    if (checkLastManStands(currentInnings, gameState)) {
      currentInnings.isLastManStands = true;
      currentInnings.batter2 = {
        name: "Last Man Stands",
        runs: 0,
        balls: 0,
        onStrike: false,
        isRetired: false,
      };
      currentInnings.batter1.onStrike = true;
    } else {
      currentInnings.batter2 = {
        name: getNextBatterName(currentBatters, currentInnings, battingTeamPlayers),
        runs: 0,
        balls: 0,
        onStrike: false,
        isRetired: false,
      };
      currentInnings.batter1.onStrike = true;
    }
  }

  // Check if over is complete (standard balls per over)
  if (currentInnings.ballsThisOver === newState.matchSettings.ballsPerOver) {
    endOver(currentInnings, bowlingTeamPlayers);
  }

  // Check if innings is complete
  if (checkInningsComplete(currentInnings, newState)) {
    if (newState.currentInnings === 1) {
      if (currentInnings.currentOverBalls.length > 0) {
        currentInnings.previousOvers.push([...currentInnings.currentOverBalls]);
      }
      return { newState, shouldStartSecondInnings: true };
    } else {
      newState.gameComplete = true;
      newState.winner = determineWinner(newState);
    }
  }

  return { newState, shouldStartSecondInnings: false };
}

function endOver(currentInnings: any, bowlingTeamPlayers: string[]) {
  currentInnings.previousOvers.push([...currentInnings.currentOverBalls]);
  
  currentInnings.ballsThisOver = 0;
  currentInnings.currentOverBalls = [];
  currentInnings.currentOver += 1;
  
  if (!currentInnings.isLastManStands) {
    currentInnings.batter1.onStrike = !currentInnings.batter1.onStrike;
    currentInnings.batter2.onStrike = !currentInnings.batter2.onStrike;
  }
  
  currentInnings.bowler = {
    name: getNextBowlerName(currentInnings.bowler.name, bowlingTeamPlayers),
    runsConceded: 0,
    ballsBowled: 0,
    wicketsTaken: 0,
  };
}